import React, { createContext, useState, useMemo, useCallback } from 'react';
import { FormQuotationInformationForm } from '../../components/QuotationInformationForm/quoation-information.interface';
import { MedicalPreScreeningForm } from '../../components/QuotationInformationForm/MedicalScreeningInformation/MedicalPreScreening';

interface CacheStoreFields {
  personalInformationState: FormQuotationInformationForm | null;
  medicalPreScreeningFormState: MedicalPreScreeningForm | null;
  isSaveQuoteAndBookingDataLoading: boolean | null;
  isQuotationInformationStepReady: boolean;
  persistQuotationInformationStep: (() => Promise<void>) | null;
  currentStep: number;
  mobileSummaryVisible: boolean;
  mobileSummaryPosition: number | null;
  tripCost: number | undefined;
  isPersonalInformationFormValid: boolean | null;
  destinationSelectionSnapshot: { value: string; label: string }[] | null;
}

export type CacheStoreCallbackResult<TK extends keyof CacheStoreFields> =
  | CacheStoreFields[TK]
  | undefined
  | null;

export type UpdateCallback<TK extends keyof CacheStoreFields> = (
  value: CacheStoreCallbackResult<TK>,
) => CacheStoreCallbackResult<TK>;
interface CacheStore {
  store: Partial<CacheStoreFields>;
  getUrlParam: (key: string) => string | null;
  setCacheValue: <TK extends keyof CacheStoreFields>(
    key: TK,
    value: CacheStoreFields[TK],
  ) => void;
  update: <TK extends keyof CacheStoreFields>(
    key: TK,
    callback: UpdateCallback<TK>,
  ) => void;
}

const CacheDataContext = createContext<CacheStore>({
  store: {},
  getUrlParam: () => null,
  setCacheValue: () => ({}),
  update: () => ({}),
});

function CacheDataProvider({
  children,
}: React.PropsWithChildren): React.JSX.Element {
  const [store, setStore] = useState<Partial<CacheStoreFields>>({});
  const [urlParams] = useState<URLSearchParams>(
    () => new URLSearchParams(window.location.search),
  );

  const getUrlParam = useCallback(
    (key: string): string | null => urlParams.get(key) ?? null,
    [urlParams],
  );

  const setCacheValue = <TK extends keyof CacheStoreFields>(
    key: TK,
    value: CacheStoreCallbackResult<TK>,
  ): void => {
    setStore((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const update = <TK extends keyof CacheStoreFields>(
    key: TK,
    callback: UpdateCallback<TK>,
  ): void => {
    const storeValue = store[key];
    const result = callback(storeValue);
    setCacheValue(key, result);
  };

  const contextValue = useMemo(
    () => ({
      store,
      getUrlParam,
      setCacheValue,
      update,
    }),
    [store],
  );

  return (
    <CacheDataContext.Provider value={contextValue}>
      {children}
    </CacheDataContext.Provider>
  );
}

export { CacheDataProvider, CacheDataContext };
